import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import AppIcon from "@/common/components/Icons/AppIcon";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { DatetimeHelper } from "@/common/helpers/datetime";
import { notificationService } from "@/common/services/notification";
import { NotificationDto, NotificationImportance } from "@/core/api/generated/v1";

import AppIconButton from "../Button/AppIconButton";
import AppTypography from "../Text/AppTypography";

interface Props {
  notification: NotificationDto;
  onMarkNotificationAsRead: (notification: NotificationDto) => void;
  onDeleteNotification: (notification: NotificationDto) => void;
  onActionExecuted?: () => void;
}

export default function NotificationListItem({
  notification,
  onMarkNotificationAsRead,
  onDeleteNotification,
  onActionExecuted,
}: Props) {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const markAsRead = () => {
    onMarkNotificationAsRead(notification);
    closeMenu();
  };

  const clear = () => {
    onDeleteNotification(notification);
    closeMenu();
  };

  const notificationAction = useMemo(
    () => notificationService.getInAppNotificationAction(notification),
    [notification],
  );

  const handleClick = () => {
    if (notificationAction.hasAction && notificationAction.executeAction) {
      notificationAction.executeAction(notification);
    }
    markAsRead();
    onActionExecuted && onActionExecuted();
  };

  if (!notification) {
    return null;
  }

  return (
    <ListItem
      sx={{
        borderLeftStyle: "solid",
        borderLeftWidth: 3,
        borderLeftColor: (theme) =>
          notification.importance === NotificationImportance.High
            ? theme.palette.error.main
            : theme.palette.divider,
      }}
      key={notification.id!}
      disablePadding
      secondaryAction={
        <AppIconButton
          edge='end'
          aria-label='actions'
          onClick={(e) => {
            setMenuAnchorEl(e.currentTarget);
          }}
        >
          <AppIcon of='moreVert' />
        </AppIconButton>
      }
    >
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        slotProps={{
          list: {
            "aria-labelledby": "menu-button",
          },
        }}
      >
        <MenuItem dense disabled={notification.isRead} onClick={markAsRead}>
          Mark as read
        </MenuItem>
        <MenuItem dense onClick={clear}>
          Clear
        </MenuItem>
      </Menu>

      <ListItemButton
        selected={notification.isRead === false}
        role={undefined}
        component={notificationAction.routeUrl ? RouterLink : Box}
        to={notificationAction.routeUrl}
        onClick={handleClick}
        sx={{ cursor: notificationAction.hasAction ? "pointer" : "default" }}
      >
        <ListItemAvatar>
          <Avatar></Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={notification.inApp?.title}
          secondary={
            <Stack direction='column' component='span'>
              <AppTypography
                variant='body2'
                ellipsing={{ enabled: true, linesToStartEllipsing: 2 }}
              >
                {notification.inApp?.body}
              </AppTypography>
              <AppTypography
                component='span'
                variant='caption'
                color='text.secondary'
                decoration={{ variant: "helpText" }}
                tooltipProps={{
                  placement: "bottom-start",
                  title: (
                    <Box>
                      At{" "}
                      {notification.createdAt &&
                        moment(notification.createdAt).format(DATETIME_FORMATS.DISPLAY_DATETIME)}
                    </Box>
                  ),
                }}
              >
                {DatetimeHelper.humanizeDateRangeDurationFromNow(notification.createdAt, {
                  isSuffix: true,
                })}
              </AppTypography>
            </Stack>
          }
          slotProps={{
            primary: { component: AppTypography, variant: "body1" },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}
